import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { ReactSVGPanZoom } from 'react-svg-pan-zoom';

import theme from '../../styles/theme';
import { 
  sum, 
  map, 
  isEqual, 
  pick, 
  reverse, 
  sortBy,
} from 'lodash';

import Cell from '../cell'
import Band from '../cell/band';
import tinycolor from 'tinycolor2';

import { getCells } from '../../core/cells';
import { getGenerations } from '../../core/generations';
import { getChart } from '../../core/chart';
import { getSelectedCell, setSelectedCell, setSelectedMenu, getInterfaceMode } from '../../core/ui';

import thematics from '../../core/thematic';

import { 
  degreesToRadians, 
  circlePathDef, 
  slicePathDef, 
  textArcPath, 
  slicePathCoordinates,
  getDetailedChart,
  getDetailedCells,
  getBandPath,
  getCellPath,
  getTextPath,
} from '../../core/utils';

const StyledChart = styled.div`
  position: fixed;
  top: 55px;
  left: 358px;
  right: 0;
  bottom: 0;
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 300;
    background-position: -30px;
    background-image: url(${props => props.url});
    background-repeat: repeat;
    pointer-events: none;
    opacity: 0.08;
  }
`;


class Fanchart extends React.Component {

  componentDidUpdate(prevProps) {
    const { chart, selected } = this.props;

    const zoomProps = ['generations', 'angle', 'angleOffset', 'margin', 'radius'];
    if(!isEqual(pick(prevProps.chart, zoomProps), pick(chart, zoomProps))) {
      this.Viewer.fitToViewer();
    }

    if(!isEqual(prevProps.selected, selected)) {
      this.Viewer.changeTool('none');
    }
  }

  componentDidMount() {
    this.Viewer.fitToViewer();
  }

  renderHeading(minX, maxX, maxY) {
    const { chart } = this.props;
    const yPos = maxY - chart.heading.fontSize / 2 - 
                 (chart.subheading ? (chart.subheading.fontSize + chart.subheading.margin) : 0)
    return this.renderText(chart.heading, minX, maxX, yPos, 'heading');
  }

  renderSubheading(minX, maxX, maxY) {
    const { chart } = this.props;
    const yPos = maxY - chart.subheading.fontSize / 2;
    return this.renderText(chart.subheading, minX, maxX, yPos, 'subheading');
  }

  renderText(text, minX, maxX, yPos, key) {
    const textPath = `M${-minX},${yPos} ${maxX},${yPos}`;
    
    return (
      <g>
        <path id={key + 'Path'} stroke="none" fill="none" d={textPath}></path>
        <text x="0" y="0" fontFamily={text.fontFamily} textAnchor='middle' fill={text.textColor} fontSize={text.fontSize} dy="0">
          <textPath xlinkHref={`#${key}Path`} startOffset="50%">
            <tspan>{text.text}</tspan>
          </textPath>
        </text>
      </g>
    )
  }

  render() {

    const { chart, generations, cells, setSelectedCell, thematic, hasHeading, hasSubheading, interfaceMode } = this.props;

    const dChart = getDetailedChart(chart, generations);    
    let { minY, maxY, maxX, minX } = dChart;
    const resizeProps = {radius: dChart.totalRadius, minY, minX, maxY, maxX, generations, chart};
    const dCells = getDetailedCells(cells, generations, dChart);

    if(thematic) {
      const _resize = thematic.resize(resizeProps);
      minY = _resize.minY;
      minX = _resize.minX;
      maxY = _resize.maxY;
      maxX = _resize.maxX;
    }

    if(hasHeading) {
      maxY += chart.heading.margin + chart.heading.fontSize;
    }
    if(hasSubheading) {
      maxY += chart.subheading.margin + chart.subheading.fontSize;
    }

    const url = interfaceMode === "rootsgifts"
      ? "https://firebasestorage.googleapis.com/v0/b/family-tree-prints.appspot.com/o/site%2Froots-gifts-watermark.png?alt=media&token=b2f2d8bd-5bac-4ac5-abac-82cb484f1731"
      : "https://firebasestorage.googleapis.com/v0/b/family-tree-prints.appspot.com/o/site%2Fwatermark.png?alt=media&token=df8921e4-c3ad-44c1-8582-f367cb75d7b3"

    const width =  minX + maxX + chart.margin * 2;
    const height = minY + maxY + chart.margin * 2;
    return (
      <StyledChart ref="window" url={url}>
        <ReactSVGPanZoom 
          miniaturePosition="none"
          width={window.innerWidth - 358} 
          height={window.innerHeight - 55} 
          detectAutoPan={false} 
          background="#eaeaea"
          ref={Viewer => { this.Viewer = Viewer }}
          className="fanchart">
          <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
            <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
              {chart.background && 
                <rect fill={chart.background} width={width} height={height} />
              }
              <g id="ex-chart" transform={`translate(${minX + chart.margin}, ${minY + chart.margin})`}>
                {thematic && thematic.component && <thematic.component {...resizeProps} cells={cells} />}

                {sortBy(dCells, c => -c.ascendancy).map(cell => {
                  const cellPath = getCellPath(cell);
                  const textPath = getTextPath(cell);
                  const generation = generations[cell.generation];
                  const date = `${cell.birth || '        '} - ${cell.death || '        '}`;
                    
                  return (
                    <Cell 
                      key={cell.ascendancy} 
                      uid={cell.ascendancy}
                      cellPath={cellPath} 
                      text={chart.textTransform === 'none' ? cell.name : (cell.name || "").toUpperCase()}
                      textPath={textPath}
                      cellType={cell.cellType}
                      manualOffset={generation.offset}
                      reverse={cell.isInverted}
                      date={date}
                      fontSize={generation.fontSize}
                      fontFamily={chart.font}
                      strokeWidth={chart.strokeWidth}
                      strokeColor={ generation.strokeVisible ? chart.strokeColor : 'rgba(0,0,0,0)'}
                      fill={cell.background}
                      textColor={cell.textColor}
                      onClick={() => setSelectedCell(cell)}
                      />
                  )
                })}

                {dCells.slice(1).map(cell => {
                  if(cell.bandColor) {
                    const bandPath = getBandPath(cell);
                    return (
                      <Band
                        key={'band-' + cell.ascendancy}
                        uid={cell.ascendancy}
                        fill={cell.bandColor}
                        cellPath={bandPath} />
                    )
                  }
                })}

                {hasHeading && this.renderHeading(minX, maxX, maxY)}
                {hasSubheading && this.renderSubheading(minX, maxX, maxY)}

                {thematic && thematic.overlay && <thematic.overlay {...resizeProps} cells={cells} />}
              </g>
            </svg>
          </svg>
        </ReactSVGPanZoom>
      </StyledChart>
    )
  }
}

const mapStateToProps = createSelector(
  getChart,
  getGenerations,
  getCells,
  getSelectedCell,
  getInterfaceMode,
  (chart, generations, cells, selected, interfaceMode) => ({
    chart,
    generations,
    cells,
    selected,
    thematic: thematics[chart.thematic],
    hasHeading: !!chart.heading.text,
    hasSubheading: !!chart.subheading.text,
    interfaceMode,
  })
)

const mapDispatchToProps = Object.assign({setSelectedCell, setSelectedMenu})

export default connect(mapStateToProps,mapDispatchToProps)(Fanchart);


// WEBPACK FOOTER //
// ./src/components/fanchart/index.js